const REPLAY_FLAG = '__stzVariantRepairReplay';
const NO_BIRTH_TAGS = ['no birth data', 'fixed graphic', 'fixed chart'];
const NO_BIRTH_TYPES = ['fixed graphic', 'builder hoodie'];

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', hideIrrelevantBirthFields, { once: true });
} else {
  hideIrrelevantBirthFields();
}

document.addEventListener(
  'change',
  async (event) => {
    if (event[REPLAY_FLAG]) return;
    if (!(event.target instanceof HTMLElement)) return;

    const picker = event.target.closest('variant-picker');
    if (!picker || event.target.closest('product-card') || event.target.closest('quick-add-dialog')) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    await repairVariantSelection(picker, event.target);

    const replay = new Event('change', { bubbles: true });
    replay[REPLAY_FLAG] = true;
    event.target.dispatchEvent(replay);
  },
  true
);

async function repairVariantSelection(picker, changedTarget) {
  const product = await getProductJson(picker);
  if (!product?.variants?.length || !product?.options?.length) return;

  const selectedOptions = getSelectedOptionsByName(picker);
  const exactVariant = findVariantForSelectedOptions(product, selectedOptions);
  if (exactVariant?.available) return;

  const repairedVariant = findNearestAvailableVariant(product, selectedOptions, changedTarget);
  if (!repairedVariant) return;

  applyVariantOptions(picker, product, repairedVariant);
}

async function getProductJson(picker) {
  if (!picker.dataset.productUrl) return null;

  picker.__stzProductJsonPromise ??= fetch(`${picker.dataset.productUrl.split('?')[0]}.js`)
    .then((response) => (response.ok ? response.json() : null))
    .catch((error) => {
      console.warn('[starlyzed-variant-repair] Could not load product JSON:', error);
      return null;
    });

  return picker.__stzProductJsonPromise;
}

function getSelectedOptionsByName(picker) {
  const selected = new Map();

  for (const select of picker.querySelectorAll('select')) {
    const option = select.selectedOptions[0];
    if (option?.dataset.optionName) selected.set(option.dataset.optionName, option.value);
  }

  for (const input of picker.querySelectorAll('fieldset input:checked')) {
    if (input.dataset.optionName) selected.set(input.dataset.optionName, input.value);
  }

  return selected;
}

function findVariantForSelectedOptions(product, selectedOptions) {
  return (
    product.variants.find((variant) => {
      return product.options.every((option, index) => {
        const selectedValue = selectedOptions.get(optionName(option));
        return !selectedValue || variantOptionValue(variant, index) === selectedValue;
      });
    }) ?? null
  );
}

function findNearestAvailableVariant(product, selectedOptions, changedTarget) {
  const changedOptionName =
    changedTarget instanceof HTMLSelectElement
      ? changedTarget.selectedOptions[0]?.dataset.optionName
      : changedTarget.dataset.optionName;
  const sizeOption = product.options.find((option) => optionName(option).toLowerCase() === 'size');
  const flexibleOptionName = optionName(sizeOption) || changedOptionName;

  return (
    product.variants.find((variant) => {
      if (!variant.available) return false;

      return product.options.every((option, index) => {
        const name = optionName(option);
        if (name === flexibleOptionName) return true;

        const selectedValue = selectedOptions.get(name);
        return !selectedValue || variantOptionValue(variant, index) === selectedValue;
      });
    }) ?? null
  );
}

function applyVariantOptions(picker, product, variant) {
  product.options.forEach((option, index) => {
    const name = optionName(option);
    const targetValue = variantOptionValue(variant, index);
    if (!name || !targetValue) return;

    const radio = Array.from(picker.querySelectorAll('input')).find((input) => {
      return input.dataset.optionName === name && input.value === targetValue;
    });

    if (radio) {
      radio.checked = true;
      return;
    }

    const select = Array.from(picker.querySelectorAll('select')).find((element) => {
      return element.selectedOptions[0]?.dataset.optionName === name;
    });

    if (select) select.value = targetValue;
  });
}

function optionName(option) {
  return typeof option === 'string' ? option : option?.name;
}

function variantOptionValue(variant, index) {
  return variant.options?.[index] ?? variant[`option${index + 1}`];
}

async function hideIrrelevantBirthFields() {
  const handle = location.pathname.match(/\/products\/([^/?#]+)/)?.[1];
  if (!handle) return;

  const product = await fetchProductJson(handle);
  if (!product || productRequiresBirthData(product)) return;

  for (const block of document.querySelectorAll('product-custom-property-component')) {
    disableAndHideBirthBlock(block);
  }

  for (const intro of document.querySelectorAll('rte-formatter')) {
    if (intro.textContent?.toLowerCase().includes('birth details')) {
      intro.hidden = true;
    }
  }

  for (const gate of document.querySelectorAll('birth-data-gate')) {
    gate.hidden = true;
  }
}

async function fetchProductJson(handle) {
  try {
    const response = await fetch(`/products/${handle}.js`, { cache: 'force-cache' });
    return response.ok ? await response.json() : null;
  } catch (error) {
    console.warn('[starlyzed-variant-repair] Could not load product JSON:', error);
    return null;
  }
}

function productRequiresBirthData(product) {
  const tags = normalizeTags(product.tags);
  const type = (product.type ?? '').toLowerCase();

  if (tags.includes('birth data required')) return true;
  if (NO_BIRTH_TAGS.some((tag) => tags.includes(tag))) return false;
  if (NO_BIRTH_TYPES.some((productType) => type.includes(productType))) return false;

  return true;
}

function normalizeTags(tags) {
  if (Array.isArray(tags)) return tags.join(',').toLowerCase();
  return String(tags ?? '').toLowerCase();
}

function disableAndHideBirthBlock(block) {
  for (const field of block.querySelectorAll('input, textarea, select')) {
    field.required = false;
    field.disabled = true;
    field.removeAttribute('required');
    field.removeAttribute('aria-required');
    field.removeAttribute('name');
  }

  block.hidden = true;
}
